export type VisitModality = 'in_person' | 'video' | 'audio' | null | undefined;
export type ConcreteVisitModality = Exclude<VisitModality, null | undefined>;

export const VISIT_MODALITY_OPTIONS: Array<{ value: ConcreteVisitModality; label: string }> = [
  { value: 'in_person', label: 'In-person' },
  { value: 'audio', label: 'Phone' },
  { value: 'video', label: 'Video' },
];

const CONCRETE_VISIT_MODALITIES = new Set<ConcreteVisitModality>(
  VISIT_MODALITY_OPTIONS.map(option => option.value),
);

export const isConcreteVisitModality = (value: unknown): value is ConcreteVisitModality =>
  typeof value === 'string' && CONCRETE_VISIT_MODALITIES.has(value as ConcreteVisitModality);

export const normalizeVisitModality = (value: unknown): ConcreteVisitModality | null =>
  isConcreteVisitModality(value) ? value : null;

const TELEHEALTH_PLACE_OF_SERVICE_CODES = new Set(['02', '10']);

export const isTelehealthModality = (modality: VisitModality): boolean =>
  modality === 'video' || modality === 'audio';

export const getTelehealthForModality = (modality: VisitModality): boolean =>
  isTelehealthModality(modality);

export const isTelehealthPlaceOfService = (placeOfService: string | null | undefined): boolean =>
  Boolean(placeOfService && TELEHEALTH_PLACE_OF_SERVICE_CODES.has(placeOfService));

export const getVisitModalityLabel = (modality: VisitModality): string => {
  const option = VISIT_MODALITY_OPTIONS.find(item => item.value === modality);
  return option?.label ?? 'Visit mode not set';
};

export const getDefaultPlaceOfServiceForModality = (modality: VisitModality): string | null => {
  switch (modality) {
    case 'in_person':
      return '11';
    case 'video':
      return '10';
    case 'audio':
      return '02';
    default:
      return null;
  }
};

export const getCoherentPlaceOfServiceForModality = ({
  modality,
  placeOfService,
}: {
  modality: VisitModality;
  placeOfService: string | null | undefined;
}): string | null => {
  if (!modality) return placeOfService || null;
  if (!placeOfService) return getDefaultPlaceOfServiceForModality(modality);

  const telehealthPos = isTelehealthPlaceOfService(placeOfService);
  if (isTelehealthModality(modality) !== telehealthPos) {
    return getDefaultPlaceOfServiceForModality(modality);
  }

  return placeOfService;
};

export const getModalityPlaceOfServiceError = ({
  modality,
  placeOfService,
}: {
  modality: VisitModality;
  placeOfService: string | null | undefined;
}): string | null => {
  if (!modality || !placeOfService) return null;

  const telehealthPos = isTelehealthPlaceOfService(placeOfService);

  if (isTelehealthModality(modality) && !telehealthPos) {
    return 'Telehealth visit modes must use POS 02 or 10.';
  }

  if (modality === 'in_person' && telehealthPos) {
    return 'In-person visit mode cannot use telehealth POS 02 or 10.';
  }

  return null;
};

const CPT_CODE_PATTERN = /^[A-Z0-9]{4,5}$/;

export const normalizeCptCode = (code: string | null | undefined): string | null => {
  const normalized = (code ?? '')
    .trim()
    .replace(/^CPT:\s*/i, '')
    .trim()
    .toUpperCase();
  const baseCode = normalized.split('-')[0].trim();
  return CPT_CODE_PATTERN.test(baseCode) ? baseCode : null;
};

export const normalizeCptCodes = (codes: string[] | null | undefined): string[] =>
  Array.from(
    new Set((codes ?? []).map(normalizeCptCode).filter((code): code is string => Boolean(code))),
  );

const normalizeComparableCptCodes = (codes: string[] | null | undefined): string[] =>
  [...normalizeCptCodes(codes)].sort();

export const doCptCodesMatch = (
  left: string[] | null | undefined,
  right: string[] | null | undefined,
): boolean =>
  JSON.stringify(normalizeComparableCptCodes(left)) ===
  JSON.stringify(normalizeComparableCptCodes(right));

export const formatSessionSummary = ({
  modality,
  placeOfService,
  cptCodes,
  useJotmeet,
}: {
  modality: VisitModality;
  placeOfService?: string | null;
  cptCodes?: string[] | null;
  useJotmeet?: boolean | null;
}): string => {
  const cleanCptCodes = normalizeCptCodes(cptCodes);
  const parts = [
    getVisitModalityLabel(modality),
    placeOfService ? `POS ${placeOfService}` : null,
    cleanCptCodes.length
      ? `${cleanCptCodes.length} CPT code${cleanCptCodes.length === 1 ? '' : 's'}`
      : null,
    useJotmeet ? 'JotMeet' : null,
  ].filter(Boolean);

  return parts.join(' · ');
};
